import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useWebEDIStore } from 'stores/webedi/webedi';
import { DATA_CHECK_ERROR_CODE, EMPTY_FUNCTION } from 'support/helpers/const/const';
import { datachecksResolver } from 'support/helpers/resolvers/datachecksResolver';
import { buildDocumentValidationKey } from 'support/helpers/resolvers/documentValidationKey';
import { MessageV2DTO, ProcessSpecificationDTO } from 'support/types';

type UseMessageViewFormArgs = {
  message: MessageV2DTO | undefined;
  flattenData: Record<string, any>;
  processSpecFields: ProcessSpecificationDTO['fields'] | undefined;
};

export const useMessageViewForm = ({ message, flattenData, processSpecFields }: UseMessageViewFormArgs) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const isValidationComplete = useWebEDIStore((state) => state.isValidationComplete);
  const dataStores = useWebEDIStore((state) => state.dataStores);
  const hasDatachecksError = message?.blocking_error?.code === DATA_CHECK_ERROR_CODE;
  const resolver = useMemo(
    () =>
      hasDatachecksError
        ? datachecksResolver({
            fieldsConfiguration: processSpecFields,
            withTimeout: false,
            dataStores,
            documentKey: buildDocumentValidationKey({ messageId: message?.id }),
          })
        : undefined,
    [processSpecFields, hasDatachecksError, dataStores, message?.id],
  );

  const formMethods = useForm<typeof flattenData>({
    values: flattenData,
    resolver,
  });

  // We need to use the message?.canonical as a dependency to reset the form
  // The message it self doesn't matter, just the data content
  useEffect(() => {
    setIsInitialized(false);
  }, [message?.canonical]);

  useEffect(() => {
    if (flattenData) {
      setIsInitialized(true);
    }
  }, [flattenData]);

  useEffect(() => {
    if (hasDatachecksError && flattenData) {
      // Validate data right away if there's a datachecks error
      // Without triggering validation, react-hook-forms won't give us the errors
      formMethods.handleSubmit(EMPTY_FUNCTION)();
    }
  }, [flattenData, formMethods, hasDatachecksError]);

  const isLoading = !flattenData || !isInitialized;

  return {
    formMethods,
    isLoading,
    isValidationComplete: hasDatachecksError ? isValidationComplete : true,
  };
};
